import { Binoculars } from "@phosphor-icons/react"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import Image from "next/image"
import { PRODUCTS } from "@/constants/urls"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"
import { useLeaderboardData } from "./useLeaderboardData"

const STATIC_ASSETS_BASE_URL = "https://static1.tokenterminal.com/"

interface AssetLogoProps {
  logo: string
  name: string
}

function AssetLogo({ logo, name }: AssetLogoProps) {
  if (!logo) {
    return (
      <div className="w-5 h-5 rounded-full bg-bg-muted flex items-center justify-center">
        <span className="text-[10px] font-medium text-fg-secondary">
          {name.charAt(0).toUpperCase()}
        </span>
      </div>
    )
  }

  const logoUrl = logo.startsWith("http") ? logo : `${STATIC_ASSETS_BASE_URL}${logo}`

  return (
    <Image
      src={logoUrl}
      alt={name}
      width={20}
      height={20}
      className="w-5 h-5 rounded-full object-cover"
      unoptimized
    />
  )
}

function LeaderboardSkeleton() {
  return (
    <div className="flex flex-col flex-1 justify-between">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-2 border-b border-border-default last:border-b-0"
        >
          <span className="w-4 h-4 bg-bg-muted rounded animate-pulse" />
          <div className="flex items-center gap-2 flex-1">
            <div className="w-5 h-5 bg-bg-muted rounded-full animate-pulse" />
            <div className="h-4 w-16 bg-bg-muted rounded animate-pulse" />
          </div>
          <div className="flex items-center gap-3">
            <div className="h-4 w-14 bg-bg-muted rounded animate-pulse" />
            <div className="h-4 w-12 bg-bg-muted rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function MasonryLeaderboard() {
  const { leaderboardData, isLoading, metricDisplayName } = useLeaderboardData()

  return (
    <MasonryWidget
      href={PRODUCTS.EXPLORER_ROUTE}
      icon={Binoculars}
      productName="Explorer"
      widgetName="Tokenized assets"
      description="Compare the top-performing tokenized assets with standardized metrics like market cap, trading volume, and price changes to identify market trends."
      featuredVisual={
        <div
          className="relative bg-bg-surface border-t border-l border-solid border-border-default rounded-tl-xl flex-1 min-h-0 pt-6 pl-6 flex flex-col overflow-hidden"
          style={FADE_MASK_STYLE}
        >
          <div className="pb-3 flex justify-between items-center shrink-0">
            <h3 className="text-sm font-medium text-fg-default">{metricDisplayName}</h3>
            <span className="text-xs text-fg-tertiary pr-3">30d</span>
          </div>

          <div className="pr-4 flex-1 min-h-0 overflow-hidden">
            {isLoading || leaderboardData.length === 0 ? (
              <LeaderboardSkeleton />
            ) : (
              <div className="flex flex-col flex-1 justify-between h-full">
                {leaderboardData.map((item) => (
                  <div
                    key={item.assetId}
                    className="flex items-center gap-3 py-2 border-b border-border-default last:border-b-0"
                  >
                    <span className="w-4 text-xs font-medium text-fg-tertiary text-right">
                      {item.rank}
                    </span>

                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <AssetLogo logo={item.logo} name={item.name} />
                      <span className="text-xs font-medium text-fg-default truncate">
                        {item.name}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-xs tabular-nums text-fg-default">
                        {formatNumber({
                          value: item.marketCap,
                          metricFormat: "number",
                          prefix: "$",
                          decimals: 1,
                          abbreviationFormat: "compact",
                        })}
                      </span>
                      <span
                        className={`text-xs tabular-nums min-w-[50px] text-right ${
                          item.change >= 0 ? "text-fg-emerald" : "text-fg-red"
                        }`}
                      >
                        {item.change >= 0 ? "+" : ""}
                        {item.change.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  )
}
